import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Loader2, RefreshCw, AlertTriangle, AlertCircle, Info, ChevronDown, ChevronRight } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import TargetPlanTab from './YatasTargetPlan';

/* ------------------------------------------------------------------ */
/*  Yataş Grubu — Medya Planlama & Asistan  (/yatas/planlama)          */
/*  Kaynak: Yataş grubu toplantısı, 25 Eyl 2026.                       */
/*   1. Bütçe planı: performans + branding bütçesi → mecra / objective  */
/*      kırılımı ve beklenen sonuç (geçmiş aylardan).                  */
/*   2. YouTube senaryosu: "TV'ye çıkmazsam YouTube'a ne kadar?"       */
/*   3. Asistan: kampanya sağlık taraması, 101 seviyesinde açıklama.   */
/*   4. GBP × lokal → 7 Eki 2026'da /gbp/lokal sayfasına taşındı.      */
/*  Backend: routes/yatasPlanning.js — Yataş dışı marka 403 alır.      */
/* ------------------------------------------------------------------ */

const API = 'https://api.glowytics.com';

const YATAS_GROUP_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', // Yataş Bedding
  'dfcf54dd-b00d-434a-be1e-27b6484848eb', // Divanev
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', // Puffy
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e', // EnzaHome
]);

const TABS = [
  { id: 'target', label: 'Hedeften Plana' },
  { id: 'plan', label: 'Bütçe Planı' },
  { id: 'youtube', label: 'YouTube Senaryosu' },
  { id: 'assistant', label: 'Asistan' },
];

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const monthLabel = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : '');
const shortMonth = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1].slice(0, 3)} ${ym.slice(2, 4)}` : '');

const tl = (v) => (v == null ? '—' : `₺${Math.round(v).toLocaleString('tr-TR')}`);
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString('tr-TR'));
const compact = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} Mn`;
  if (a >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} B`;
  return Math.round(v).toLocaleString('tr-TR');
};
const dec = (v, k = 2) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: k, maximumFractionDigits: k }));

function nextMonthYm() {
  const d = new Date();
  const n = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

async function getJson(path, brandId, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include', headers: { 'X-Brand-ID': brandId }, ...opts,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-800 mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function MoneyInput({ label, value, onChange, disabled, hint }) {
  return (
    <label className="flex flex-col text-xs text-gray-500 gap-1">
      {label}
      <div className={`flex items-center border border-gray-200 rounded-md px-2 focus-within:border-blue-400 ${disabled ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
        <span className="text-gray-400 text-sm">₺</span>
        <input
          type="text" inputMode="numeric"
          value={value ? Number(value).toLocaleString('tr-TR') : ''}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, '')) || 0)}
          className="w-36 px-1 py-1.5 text-sm text-gray-800 focus:outline-none"
        />
      </div>
      {hint && <span className="text-[11px] text-gray-400 max-w-[220px]">{hint}</span>}
    </label>
  );
}

/* ------------------------------ Bütçe Planı ------------------------------ */

function ShareBar({ hist, next }) {
  const up = next - hist;
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 bg-gray-100 rounded relative">
        <div className="absolute inset-y-0 left-0 bg-gray-300 rounded" style={{ width: `${Math.min(hist, 100)}%` }} />
        <div className="absolute inset-y-0 left-0 bg-blue-500 rounded" style={{ width: `${Math.min(next, 100)}%`, opacity: 0.85 }} />
      </div>
      <span className={`text-xs tabular-nums w-12 text-right ${Math.abs(up) < 1 ? 'text-gray-400' : up > 0 ? 'text-green-600' : 'text-amber-600'}`}>
        {up > 0 ? '+' : ''}{dec(up, 1)}
      </span>
    </div>
  );
}

function PerformanceTable({ perf }) {
  if (perf?.reason) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900">
        {perf.note || 'Bu markada performans (ciro) bazlı tahmin yapılamıyor.'}
      </div>
    );
  }
  if (!perf?.lines?.length) {
    return <div className="text-sm text-gray-400 p-4">Bu markada son aylarda performans etiketli harcama yok.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-3">Mecra · Objective</th>
            <th className="py-2 pr-3 text-right">Bütçe</th>
            <th className="py-2 pr-3">Pay (gri = geçmiş, mavi = öneri)</th>
            <th className="py-2 pr-3 text-right">Geçmiş ROAS</th>
            <th className="py-2 pr-3 text-right">Beklenen ROAS</th>
            <th className="py-2 pr-3 text-right">Beklenen ciro</th>
            <th className="py-2 pr-3 text-right">Aralık</th>
            <th className="py-2">Neden</th>
          </tr>
        </thead>
        <tbody>
          {perf.lines.map((l) => (
            <tr key={l.key} className="border-b border-gray-100 align-top">
              <td className="py-2 pr-3">
                <div className="font-medium text-gray-800">{l.platform} · {l.objective}</div>
                <div className="text-xs text-gray-400">{l.campaigns} kampanya · geçmiş ort. {tl(l.historicalAvgMonthly)}/ay</div>
              </td>
              <td className="py-2 pr-3 text-right font-semibold tabular-nums">{tl(l.budget)}</td>
              <td className="py-2 pr-3"><ShareBar hist={l.historicalShare} next={l.share} /></td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{dec(l.historicalRoas)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{dec(l.forecast.roas)}</td>
              <td className="py-2 pr-3 text-right tabular-nums font-medium">{tl(l.forecast.revenue)}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-xs text-gray-500 whitespace-nowrap">
                {compact(l.forecast.revenueLow)} – {compact(l.forecast.revenueHigh)}
              </td>
              <td className="py-2 text-xs text-gray-500">{l.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BrandingTable({ branding }) {
  if (!branding?.lines?.length) {
    return <div className="text-sm text-gray-400 p-4">Bu markada son aylarda branding etiketli harcama yok.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-2 pr-3">Mecra · Objective</th>
            <th className="py-2 pr-3 text-right">Bütçe</th>
            <th className="py-2 pr-3">Pay (gri = geçmiş, mavi = öneri)</th>
            <th className="py-2 pr-3 text-right">Geçmiş CPM</th>
            <th className="py-2 pr-3 text-right">Beklenen gösterim</th>
            <th className="py-2 pr-3 text-right">Video izlenme</th>
            <th className="py-2 pr-3 text-right">Siteye oturum</th>
            <th className="py-2">Neden</th>
          </tr>
        </thead>
        <tbody>
          {branding.lines.map((l) => (
            <tr key={l.key} className="border-b border-gray-100 align-top">
              <td className="py-2 pr-3">
                <div className="font-medium text-gray-800">{l.platform} · {l.objective}</div>
                <div className="text-xs text-gray-400">{l.campaigns} kampanya · geçmiş ort. {tl(l.historicalAvgMonthly)}/ay</div>
              </td>
              <td className="py-2 pr-3 text-right font-semibold tabular-nums">{tl(l.budget)}</td>
              <td className="py-2 pr-3"><ShareBar hist={l.historicalShare} next={l.share} /></td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{l.historicalCpm != null ? `₺${dec(l.historicalCpm, 1)}` : '—'}</td>
              <td className="py-2 pr-3 text-right tabular-nums font-medium">{compact(l.forecast.impressions)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{compact(l.forecast.views)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{compact(l.forecast.sessions)}</td>
              <td className="py-2 text-xs text-gray-500">{l.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanTab({ brandId }) {
  const [perf, setPerf] = useState(2000000);
  const [brand, setBrand] = useState(1000000);
  const [month, setMonth] = useState(nextMonthYm());
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getJson(`/api/yatas-planning/plan?perf=${perf}&brand=${brand}&month=${month}&months=${months}`, brandId));
    } catch (e) {
      toast.error('Plan hesaplanamadı: ' + e.message);
    } finally { setLoading(false); }
  }, [brandId, perf, brand, month, months]);

  useEffect(() => { run(); }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthOptions = [0, 1, 2, 3].map((k) => {
    const d = new Date(); const n = new Date(d.getFullYear(), d.getMonth() + k, 1);
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });

  const p = data?.performance;
  const b = data?.branding;
  const hist = data?.history;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-4">
        <MoneyInput label="Performans bütçesi" value={perf} onChange={setPerf}
          disabled={!!data?.performance?.reason}
          hint={data?.performance?.reason ? 'Bu markada tahmine girmez (GA4 cirosu yok)' : null} />
        <MoneyInput label="Branding bütçesi" value={brand} onChange={setBrand} />
        <label className="flex flex-col text-xs text-gray-500 gap-1">
          Plan ayı
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 bg-white">
            {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-500 gap-1">
          Baz alınan geçmiş
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
            className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-800 bg-white">
            {[3, 6, 9, 12].map((m) => <option key={m} value={m}>Son {m} ay</option>)}
          </select>
        </label>
        <button onClick={run} disabled={loading}
          className="flex items-center gap-1.5 bg-blue-600 text-white text-sm px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Planı hesapla
        </button>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin mr-2" /> Hesaplanıyor…</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label={`${monthLabel(data.planMonth)} · beklenen ciro (performans)`}
              value={p?.totals ? compact(p.totals.revenue) : '—'}
              sub={p?.totals ? `aralık ${compact(p.totals.revenueLow)} – ${compact(p.totals.revenueHigh)}` : p?.reason ? 'GA4 cirosu yok' : 'performans harcaması yok'} />
            <Stat label="Beklenen ROAS (performans)" value={p?.totals ? dec(p.totals.roas) : '—'}
              sub={p?.poolRoas != null ? `geçmiş ${hist.months} ay: ${dec(p.poolRoas)}` : p?.reason ? 'GA4 cirosu yok' : null} />
            <Stat label="Beklenen gösterim (branding)" value={b?.totals ? compact(b.totals.impressions) : '—'}
              sub={b?.totals?.views ? `${compact(b.totals.views)} video izlenme` : null} />
            <Stat label="Beklenen oturum (toplam)" value={compact((p?.totals?.sessions || 0) + (b?.totals?.sessions || 0))}
              sub="GA4, kampanya adıyla eşleşen" />
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-xs text-blue-800">
            <b>Mevsim etkisi:</b> {data.season?.note}
            {data.season?.index && data.season.index !== 1 ? ` → ROAS tahminleri ×${dec(data.season.index)} ile çarpıldı.` : ''}
          </div>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="font-semibold text-gray-800">Performans{p?.reason ? '' : ` · ${tl(perf)}`}</h2>
              <span className="text-xs text-gray-400">{p?.reason ? 'Tahmine dahil değil' : 'Hedef: satış (GA4 cirosu)'}</span>
            </div>
            <PerformanceTable perf={p} />
            {p?.excluded?.length > 0 && (
              <div className="text-xs text-gray-400 mt-2">Payı %1'in altında olduğu için plana alınmadı: {p.excluded.join(', ')}</div>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="font-semibold text-gray-800">Branding · {tl(brand)}</h2>
              <span className="text-xs text-gray-400">Hedef: erişim (gösterim / izlenme)</span>
            </div>
            <BrandingTable branding={b} />
            {b?.excluded?.length > 0 && (
              <div className="text-xs text-gray-400 mt-2">Payı %1'in altında olduğu için plana alınmadı: {b.excluded.join(', ')}</div>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <button onClick={() => setShowHistory((v) => !v)} className="flex items-center gap-1 font-semibold text-gray-800">
              {showHistory ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Plan neye dayanıyor? · {monthLabel(hist.from)} – {monthLabel(hist.to)}
            </button>
            {showHistory && (
              <div className="mt-3 space-y-4">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hist.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="l" tickFormatter={compact} tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="r" orientation="right" tickFormatter={compact} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => tl(v)} labelFormatter={monthLabel} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar isAnimationActive={false} yAxisId="l" dataKey="performans" name="Performans harcaması" stackId="a" fill="#3b82f6" />
                      <Bar isAnimationActive={false} yAxisId="l" dataKey="branding" name="Branding harcaması" stackId="a" fill="#a78bfa" />
                      <Line isAnimationActive={false} yAxisId="r" dataKey="revenue" name="Kampanyalı GA4 cirosu" stroke="#16a34a" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1.5 pr-3">Tür</th><th className="py-1.5 pr-3">Mecra · Objective</th>
                        <th className="py-1.5 pr-3 text-right">Harcama</th><th className="py-1.5 pr-3 text-right">Aylık ort.</th>
                        <th className="py-1.5 pr-3 text-right">GA4 ciro</th><th className="py-1.5 pr-3 text-right">ROAS</th>
                        <th className="py-1.5 pr-3 text-right">CPM</th><th className="py-1.5 pr-3 text-right">CPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hist.lines.map((l) => (
                        <tr key={l.key} className="border-b border-gray-50">
                          <td className="py-1.5 pr-3 capitalize text-gray-500">{l.category === 'diger' ? 'etiketsiz' : l.category}</td>
                          <td className="py-1.5 pr-3 text-gray-800">{l.platform} · {l.objective}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{tl(l.cost)}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{tl(l.avgMonthlyCost)}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{tl(l.revenue)}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{dec(l.roas)}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{l.cpm != null ? `₺${dec(l.cpm, 1)}` : '—'}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{l.cpc != null ? `₺${dec(l.cpc)}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="text-xs text-gray-500 list-disc pl-5 space-y-1">
                  {data.method.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* ------------------------------ YouTube ------------------------------ */

function YoutubeTab({ brandId }) {
  const [budget, setBudget] = useState(500000);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getJson(`/api/yatas-planning/plan?youtube=${budget}`, brandId);
      setData(d.youtube);
    } catch (e) {
      toast.error('Senaryo hesaplanamadı: ' + e.message);
    } finally { setLoading(false); }
  }, [brandId, budget]);

  useEffect(() => { run(); }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-3">
          "Bu ay televizyona çıkmıyorum, YouTube'a ne kadar koymalıyım?" sorusu için: YouTube bütçesi girin, markanın
          kendi geçmiş YouTube maliyetleriyle kaç gösterim ve izlenme alacağını görün.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <MoneyInput label="YouTube bütçesi (aylık)" value={budget} onChange={setBudget} />
          <button onClick={run} disabled={loading}
            className="flex items-center gap-1.5 bg-blue-600 text-white text-sm px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Hesapla
          </button>
        </div>
      </div>

      {data === null && !loading && (
        <div className="text-sm text-gray-400 p-4">Bu markada son aylarda YouTube harcaması yok, senaryo kurulamıyor.</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Beklenen gösterim" value={compact(data.requested?.impressions)} sub={`CPM ≈ ₺${dec(data.requested?.cpm, 1)}`} />
            <Stat label="Beklenen izlenme" value={compact(data.requested?.views)} sub={data.historicalCpv ? `geçmiş CPV ₺${dec(data.historicalCpv)}` : null} />
            <Stat label="Geçmiş aylık YouTube harcaması" value={tl(data.historicalAvgMonthly)} sub="son 6 ay ortalaması" />
            <Stat label="Girilen / geçmiş" value={data.historicalAvgMonthly ? `×${dec(budget / data.historicalAvgMonthly, 1)}` : '—'}
              sub="1'in üstü = alışılmışın üstünde baskı" />
          </div>
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="font-semibold text-gray-800 mb-2">Bütçe arttıkça erişim nasıl değişir?</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="budget" tickFormatter={(v) => `₺${compact(v)}`} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => num(v)} labelFormatter={(v) => `Bütçe ${tl(v)}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar isAnimationActive={false} dataKey="impressions" name="Gösterim" fill="#3b82f6" />
                  <Bar isAnimationActive={false} dataKey="views" name="İzlenme" fill="#a78bfa" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-500 mt-2">{data.note}</p>
          </section>
        </>
      )}
    </div>
  );
}

/* ------------------------------ Asistan ------------------------------ */

const SEVERITY = {
  yuksek: { label: 'Acil', cls: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', Icon: AlertTriangle, iconCls: 'text-red-500' },
  orta: { label: 'Takip et', cls: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', Icon: AlertCircle, iconCls: 'text-amber-500' },
  dusuk: { label: 'Bilgi', cls: 'bg-gray-50 border-gray-200', badge: 'bg-gray-100 text-gray-600', Icon: Info, iconCls: 'text-gray-400' },
};

function Finding({ f }) {
  const [open, setOpen] = useState(false);
  const s = SEVERITY[f.severity] || SEVERITY.dusuk;
  return (
    <div className={`border rounded-lg p-3 ${s.cls}`}>
      <div className="flex items-start gap-2">
        <s.Icon size={16} className={`mt-0.5 shrink-0 ${s.iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-800 text-sm">{f.title}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.badge}`}>{s.label}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border text-gray-500">{f.platform}</span>
            <span className="text-[10px] text-gray-400 capitalize">{f.category === 'diger' ? 'etiketsiz' : f.category}</span>
          </div>
          <div className="text-xs text-gray-600 mt-0.5 break-words">{f.campaign}</div>
          <div className="text-sm text-gray-700 mt-1">{f.detail} <span className="text-xs text-gray-400">· son 7 gün harcama {tl(f.cost7)}</span></div>
          {f.explain && (
            <button onClick={() => setOpen((v) => !v)} className="text-xs text-blue-600 mt-1 hover:underline">
              {open ? 'Açıklamayı gizle' : 'Bu ne demek?'}
            </button>
          )}
          {open && <div className="text-xs text-gray-600 mt-1 bg-white/70 rounded p-2">{f.explain}</div>}
        </div>
      </div>
    </div>
  );
}

function AssistantTab({ brandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('onemli');

  const run = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getJson('/api/yatas-planning/assistant', brandId));
    } catch (e) {
      toast.error('Tarama yapılamadı: ' + e.message);
    } finally { setLoading(false); }
  }, [brandId]);

  useEffect(() => { run(); }, [run]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin mr-2" /> Kampanyalar taranıyor…</div>;
  }
  if (!data) return null;

  const counts = data.findings.reduce((a, f) => ({ ...a, [f.severity]: (a[f.severity] || 0) + 1 }), {});
  const list = filter === 'all' ? data.findings
    : filter === 'onemli' ? data.findings.filter((f) => f.severity !== 'dusuk')
      : data.findings.filter((f) => f.severity === filter);
  const o = data.overall || {};
  const roasChange = o.roas7 != null && o.roas28 ? (o.roas7 / o.roas28 - 1) * 100 : null;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          <b className="text-gray-800">{data.scanned}</b> kampanya tarandı · son 7 gün ({data.window?.last7}), önceki 28 günle
          ({data.window?.baseline}) kıyaslandı.
        </div>
        <button onClick={run} disabled={loading}
          className="flex items-center gap-1.5 text-sm border border-gray-200 px-3 py-1.5 rounded-md hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Yeniden tara
        </button>
      </div>

      {data.reason && data.note && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-900">
          {data.note} ROAS tabanlı kurallar bu markada çalışmıyor.
        </div>
      )}

      {o.roas28 != null && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Performans ROAS · son 7 gün" value={dec(o.roas7)}
            sub={roasChange != null ? `önceki 28 gün ${dec(o.roas28)} (${roasChange > 0 ? '+' : ''}${dec(roasChange, 0)}%)` : null} />
          <Stat label="Performans harcaması · son 7 gün" value={tl(o.performanceCost7)} sub={`önceki günlük ort. ${tl(o.performanceDaily28)}`} />
          <Stat label="Acil" value={counts.yuksek || 0} sub="hemen bakılmalı" />
          <Stat label="Takip et" value={counts.orta || 0} sub={`${counts.dusuk || 0} bilgi notu`} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-sm">
        {[['onemli', `Önemli (${(counts.yuksek || 0) + (counts.orta || 0)})`], ['all', `Tümü (${data.findings.length})`], ['yuksek', `Acil (${counts.yuksek || 0})`], ['orta', `Takip et (${counts.orta || 0})`], ['dusuk', `Bilgi (${counts.dusuk || 0})`]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1 rounded-full border ${filter === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {list.length === 0
        ? <div className="text-sm text-gray-400 p-4">Bu grupta bulgu yok.</div>
        : <div className="space-y-2">{list.map((f, i) => <Finding key={`${f.campaign}-${f.type}-${i}`} f={f} />)}</div>}

      <p className="text-xs text-gray-400">
        Kurallar: 7 günde ≥₺10.000 harcayıp GA4 cirosu olmayan performans kampanyası · ROAS'ta ≥%40 düşüş · CPC / CPM'de ≥%40 artış ·
        günlük harcamada ×1,6 sıçrama · son 3 gün harcamayı kesen kampanya. Ciro GA4 son tıklamadır; platform panelindeki dönüşümden farklı olabilir.
      </p>
    </div>
  );
}

/* ------------------------------ Sayfa ------------------------------ */

export default function YatasPlanning() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const isYatas = brandId && YATAS_GROUP_BRAND_IDS.has(brandId);
  const [tab, setTab] = useState('target');

  if (!isYatas) {
    return <div className="p-8 text-center text-gray-400 text-sm">Bu sayfaya erişim yetkiniz bulunmuyor.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-800">Medya Planlama & Asistan</h1>
        <p className="text-sm text-gray-500">{selectedBrand?.name} · hedef önerisi, hedefe gereken bütçe, bütçe dağılımı ve kampanya sağlık taraması</p>
      </div>
      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t.id ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {/* key={brandId}: marka değişince sekme durumu sıfırlansın */}
      {tab === 'target' && <TargetPlanTab key={brandId} brandId={brandId} />}
      {tab === 'plan' && <PlanTab key={brandId} brandId={brandId} />}
      {tab === 'youtube' && <YoutubeTab key={brandId} brandId={brandId} />}
      {tab === 'assistant' && <AssistantTab key={brandId} brandId={brandId} />}
    </div>
  );
}
